import React from 'react'
import { SelectDropdown, type SelectOption } from '../../../components/SelectDropdown/SelectDropdown'
import {
  DAYS_IN_MONTH,
  type FrequencyUnit,
  FrequencyUnits,
  MONTH_LABELS,
  type ScheduledQueryFrequency,
  WEEKDAY_LABELS,
  ordinal,
} from './scheduledQueryFrequency'

const UNIT_OPTIONS: SelectOption[] = [
  { id: FrequencyUnits.WEEK, label: 'weekly' },
  { id: FrequencyUnits.MONTH, label: 'monthly' },
  { id: FrequencyUnits.YEAR, label: 'yearly' },
]

const WEEKDAY_OPTIONS: SelectOption[] = [1, 2, 3, 4, 5, 6, 0].map((day) => ({ id: String(day), label: WEEKDAY_LABELS[day] }))

const DAY_OF_MONTH_OPTIONS: SelectOption[] = Array.from({ length: 31 }, (_, index) => ({ id: String(index + 1), label: ordinal(index + 1) }))

const MONTH_OPTIONS: SelectOption[] = Object.entries(MONTH_LABELS).map(([month, label]) => ({ id: month, label }))

interface ScheduledQueryFrequencyPickerProps {
  frequency: ScheduledQueryFrequency
  onChange: (frequency: ScheduledQueryFrequency) => void
}

export const ScheduledQueryFrequencyPicker: React.FC<ScheduledQueryFrequencyPickerProps> = ({ frequency, onChange }) => {
  const yearlyDayOptions = DAY_OF_MONTH_OPTIONS.slice(0, DAYS_IN_MONTH[frequency.month])

  const handleMonthChange = (month: number) => {
    onChange({ ...frequency, month, dayOfMonth: Math.min(frequency.dayOfMonth, DAYS_IN_MONTH[month]) })
  }

  return (
    <div className='d-flex align-items-center gap-2 flex-wrap'>
      <span className='paragraph-medium'>Start</span>
      <div className='scheduled-query-frequency-select'>
        <SelectDropdown
          options={UNIT_OPTIONS}
          initialSelectedOption={UNIT_OPTIONS.find((option) => option.id === frequency.unit) ?? null}
          hideClearButton
          onOptionSelected={(option) => onChange({ ...frequency, unit: option.id as FrequencyUnit })}
        />
      </div>
      {frequency.unit === FrequencyUnits.WEEK ? (
        <>
          <span className='paragraph-medium'>on</span>
          <div className='scheduled-query-frequency-select'>
            <SelectDropdown
              options={WEEKDAY_OPTIONS}
              initialSelectedOption={WEEKDAY_OPTIONS.find((option) => option.id === String(frequency.dayOfWeek)) ?? null}
              hideClearButton
              onOptionSelected={(option) => onChange({ ...frequency, dayOfWeek: Number(option.id) })}
            />
          </div>
        </>
      ) : null}
      {frequency.unit === FrequencyUnits.MONTH ? (
        <>
          <span className='paragraph-medium'>on the</span>
          <div className='scheduled-query-frequency-select'>
            <SelectDropdown
              options={DAY_OF_MONTH_OPTIONS}
              initialSelectedOption={DAY_OF_MONTH_OPTIONS.find((option) => option.id === String(frequency.dayOfMonth)) ?? null}
              hideClearButton
              onOptionSelected={(option) => onChange({ ...frequency, dayOfMonth: Number(option.id) })}
            />
          </div>
        </>
      ) : null}
      {frequency.unit === FrequencyUnits.YEAR ? (
        <>
          <span className='paragraph-medium'>on</span>
          <div className='scheduled-query-frequency-select'>
            <SelectDropdown
              options={MONTH_OPTIONS}
              initialSelectedOption={MONTH_OPTIONS.find((option) => option.id === String(frequency.month)) ?? null}
              hideClearButton
              onOptionSelected={(option) => handleMonthChange(Number(option.id))}
            />
          </div>
          <div className='scheduled-query-frequency-select'>
            <SelectDropdown
              options={yearlyDayOptions}
              initialSelectedOption={yearlyDayOptions.find((option) => option.id === String(frequency.dayOfMonth)) ?? null}
              hideClearButton
              onOptionSelected={(option) => onChange({ ...frequency, dayOfMonth: Number(option.id) })}
            />
          </div>
        </>
      ) : null}
    </div>
  )
}
